import React, { useState, useEffect } from 'react';

import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import IconButton from '@mui/material/IconButton';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import Edit from '../form/Edit';

import { IRole, IPermission } from '../../../../types/user';
import useAxiosFunction from '../../../../hooks/useAxiosFunction';
import useAxios from '../../../../hooks/useAxios';

export interface IProps {
    roleId: string;
    structures: Array<{_id: string, name: string}>;
    refetch: () => void;
}

const Row: React.FC<IProps> = ({ roleId, structures, refetch }) => {
    const [role, setRole] = useState<IRole>();
    const [permissions, setPermissions] = useState<Array<IPermission>>();
    const [open, setOpen] = useState<boolean>(false);

    const {
        response: responseRole,
        loading: loadingRole,
        error: errorRole,
        refetch: refetchRole
    } = useAxios({
        url: `/api/roles/${roleId}`,
        method: 'get'
    });

    const {
        loading: loadingPermiSave,
        axiosFetch: putPermiSave
    } = useAxiosFunction();

    const {
        axiosFetch: removeFetch
    } = useAxiosFunction();

    const handleChangePermission = async (
        permissionId: string, property: string, value: boolean
    ) => {
        await putPermiSave({
            url: `/api/roles/${permissionId}/permissions`,
            method: 'put',
            data: { [property]: value }
        });
        refetchRole();
    };

    const getPermissionByStructure = (structureId: string): IPermission | undefined => {
        if (!permissions || permissions.length === 0) { return undefined; }

        const strPermission = permissions.find((
            permission: IPermission
        ) => permission.structureId === structureId);

        return strPermission;
    };

    const printPermission = (structureId: string) => {
        const permission = getPermissionByStructure(structureId);

        if (!permission) { return undefined; }

        return (
            <>
                <IconButton
                    onClick={() => {
                        handleChangePermission(permission._id, 'canRead', !permission.canRead);
                    }}
                    disabled={loadingPermiSave || loadingRole}
                    aria-label="view"
                >
                    {permission.canRead ? (<VisibilityIcon />) : (<VisibilityOffIcon />)}
                </IconButton>
                <IconButton
                    onClick={() => {
                        handleChangePermission(permission._id, 'canEdit', !permission.canEdit);
                    }}
                    disabled={loadingPermiSave || loadingRole}
                    aria-label="edit"
                >
                    {permission.canEdit ? (<EditIcon />) : (<EditOffIcon />)}
                </IconButton>
                <IconButton
                    onClick={() => {
                        handleChangePermission(permission._id, 'canCreate', !permission.canCreate);
                    }}
                    disabled={loadingPermiSave || loadingRole}
                    aria-label="create"
                >
                    {permission.canCreate ? (<PlaylistAddIcon />) : (<PlaylistRemoveIcon />)}
                </IconButton>
                <IconButton
                    onClick={() => {
                        handleChangePermission(permission._id, 'canRemove', !permission.canRemove);
                    }}
                    disabled={loadingPermiSave || loadingRole}
                    aria-label="remove"
                >
                    {permission.canRemove ? (<DeleteIcon />) : (<DeleteForeverIcon />)}
                </IconButton>
            </>
        );
    };

    const handleRemove = async () => {
        await removeFetch({
            url: `/api/roles/${roleId}`,
            method: 'delete'
        });

        refetch();
    };

    useEffect(() => {
        if (responseRole) {
            setRole(() => responseRole.role);
            setPermissions(() => responseRole.permissions);
        }
    }, [responseRole]);

    return (
        <>
            { !loadingRole && !errorRole && role && (
                <TableRow key={role._id}>
                    <TableCell>
                        <IconButton
                            component="span"
                            onClick={handleRemove}
                        >
                            <DeleteIcon />
                        </IconButton>
                        <IconButton
                            component="span"
                            onClick={() => { setOpen(true); }}
                        >
                            <EditIcon />
                        </IconButton>
                    </TableCell>
                    <TableCell>
                        <strong>{role.name}</strong>
                    </TableCell>
                    { structures && structures.map((str) => (
                        <TableCell key={`${role._id}-${str._id}-cell`}>
                            {printPermission(str._id)}
                        </TableCell>
                    )) }
                    { open && (
                        <Edit
                            open={open}
                            initialName={role.name}
                            initialAdmin={role.isPlatformAdmin || false}
                            roleId={role._id}
                            onClose={() => { setOpen(false); }}
                            onSubmit={() => { refetchRole(); setOpen(false); }}
                            title="Editar rol"
                        />
                    )}
                </TableRow>
            )}
        </>
    );
};

export default Row;
